import React from "react";
import {
  MapPin,
  Users,
  Route,
  Train,
  Zap,
  Droplets,
  Plane,
  Wifi,
  Building2,
  GraduationCap,
  Factory,
  Globe2,
  Sparkles,
} from "lucide-react";
import { PageHeader, SectionCard } from "@/components/Shared";
import SouthAfricaMap from "@/components/SouthAfricaMap";
import {
  regionalLocation,
  labourPool,
  infrastructure,
  valueChain,
  strategicPositioning,
} from "@/data/extraData";

const ICONS = { Route, Train, Zap, Droplets, Plane, Wifi };
const qualityColor = {
  good: "text-emerald-700 bg-emerald-50 border-emerald-200",
  average: "text-amber-700 bg-amber-50 border-amber-200",
  poor: "text-red-700 bg-red-50 border-red-200",
};

export default function RegionalContext() {
  return (
    <div data-testid="page-regional">
      <PageHeader
        eyebrow="Regional Context & Economic Ecosystem"
        title="Where the asset sits — and why that matters."
        subtitle="Location, labour pool, infrastructure, value chain and the strategic case for long-term operations from Tzaneen."
      />

      {/* 1. Location Overview with map */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard
          title="Location Overview"
          description={regionalLocation.region}
          action={<MapPin size={16} className="text-amber-700" />}
          className="xl:col-span-2"
        >
          <div className="h-[420px]" data-testid="regional-map">
            <SouthAfricaMap />
          </div>
        </SectionCard>

        <SectionCard title="Nearby Towns & Hubs" description="Service centres within reach">
          <div className="space-y-3" data-testid="nearby-towns">
            {regionalLocation.nearestTowns.map((t) => (
              <div key={t.name} className="border-l-2 border-amber-700 pl-3 py-1">
                <div className="flex items-center justify-between">
                  <span className="font-display text-sm font-semibold text-slate-900">{t.name}</span>
                  <span className="text-xs font-mono text-slate-500">{t.distance}</span>
                </div>
                <div className="text-xs text-slate-600 mt-0.5">{t.role}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* 2. Population & Labour Pool */}
      <SectionCard
        title="Population & Labour Pool"
        description="A young, mining-adjacent workforce with deep local roots"
        className="mb-6"
        action={<Users size={16} className="text-slate-700" />}
      >
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="labour-pool-stats">
          <Stat label="District Population" value={labourPool.districtPopulation} icon={Users} />
          <Stat label="Working Age" value={labourPool.workingAge} icon={Users} />
          <Stat label="Mining Sector" value={labourPool.miningEmploymentLocal} icon={Factory} />
          <Stat label="Literacy Rate" value={labourPool.literacyRate} icon={GraduationCap} />
          <Stat label="Technical Colleges" value={`${labourPool.technicalCollegesNearby} nearby`} icon={Building2} />
        </div>
        <p className="mt-5 text-sm text-slate-700 leading-relaxed border-t border-slate-100 pt-5">
          {labourPool.notes}
        </p>
      </SectionCard>

      {/* 3. Infrastructure */}
      <SectionCard
        title="Infrastructure"
        description="Physical and digital backbone that supports operations"
        className="mb-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="infrastructure-grid">
          {infrastructure.map((it) => {
            const Icon = ICONS[it.icon] || Route;
            return (
              <div key={it.label} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center">
                    <Icon size={16} className="text-slate-700" />
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${qualityColor[it.quality]}`}>
                    {it.quality}
                  </span>
                </div>
                <div className="font-display text-sm font-semibold text-slate-900">{it.label}</div>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{it.detail}</p>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* 4. Value Chain */}
      <SectionCard
        title="Value Chain & Support Ecosystem"
        description="Suppliers, services, off-take and future opportunities"
        className="mb-6"
        action={<Globe2 size={16} className="text-amber-700" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="value-chain">
          {valueChain.map((tier, i) => (
            <div
              key={tier.tier}
              data-testid={`value-chain-${i}`}
              className={`rounded-lg p-4 border ${i === valueChain.length - 1 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="font-display text-2xl font-bold tabular-nums text-amber-700">0{i + 1}</span>
                <h4 className="font-display text-sm font-semibold text-slate-900">{tier.tier}</h4>
              </div>
              <ul className="space-y-1.5">
                {tier.items.map((it) => (
                  <li key={it} className="text-xs text-slate-700 flex items-start gap-1.5">
                    <span className="mt-1 h-1 w-1 rounded-full bg-slate-400 flex-shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* 5. Strategic Positioning */}
      <SectionCard
        title={strategicPositioning.title}
        description="The location-specific advantages that compound over a 10-year hold"
        action={<Sparkles size={16} className="text-amber-700" />}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="strategic-positioning">
          {strategicPositioning.pillars.map((p, i) => (
            <div key={p.title} className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display text-3xl font-bold text-amber-700 tabular-nums">{i + 1}</span>
                <h4 className="font-display text-base font-semibold text-slate-900">{p.title}</h4>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-slate-50/60 border border-slate-100 rounded-md p-3">
      <Icon size={14} className="text-amber-700 mb-2" />
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-base font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
